/**
 * The key figures at the top of a cellar's home page (owner feedback, 2026-09-28; changed
 * 2026-10-03): the bottles left out of the bottles left plus those drunk from the cellar, for
 * all wines and for each type (rosé, white, red and late harvest since 2026-10-15; the average
 * rating is no longer shown), and the bottles
 * in stock past their date. A tasting outside the cellar (`consumedFromStock: false`) counts in
 * neither.
 */
import { wineStock, type Cellar, type Wine } from './cellar';
import { bottlesDrunk } from './stock';
import { compareMonths, currentMonth, type MonthValue } from './months';

/** "3 / 12 bouteilles": bottles left, and bottles left plus drunk. */
export interface BottleCount {
  available: number;
  total: number;
}

/** The wine types with their own figure, in the home page's order (owner, 2026-10-15). */
export const METRIC_TYPES = ['rose', 'blanc', 'rouge', 'vendanges_tardives'] as const;
export type MetricType = (typeof METRIC_TYPES)[number];

export interface CellarMetrics {
  bottles: BottleCount;
  byType: Record<MetricType, BottleCount>;
  /** Bottles in stock whose "à boire avant" date is passed. */
  expiredBottles: number;
}

/**
 * Whether today is after the wine's drink-until date, the end of December of its year
 * (data-model.md "Drink-by state", due date). A wine with no date is never past it.
 */
export function isPastDrinkUntil(wine: Wine, now: MonthValue = currentMonth()): boolean {
  if (wine.drinkUntilYear === undefined) return false;
  const due = { year: wine.drinkUntilYear, month: 12 };
  return compareMonths(now, due) > 0;
}

const zero = (): BottleCount => ({ available: 0, total: 0 });

export function cellarMetrics(cellar: Cellar, now: MonthValue = currentMonth()): CellarMetrics {
  const bottles = zero();
  const byType = Object.fromEntries(METRIC_TYPES.map((type) => [type, zero()])) as Record<
    MetricType,
    BottleCount
  >;
  let expiredBottles = 0;

  for (const wine of cellar.wines) {
    const available = wineStock(wine);
    const drunk = wine.tastings.reduce((sum, tasting) => sum + bottlesDrunk(tasting), 0);
    const counts = [bottles];
    if ((METRIC_TYPES as readonly string[]).includes(wine.typeDeVin)) {
      counts.push(byType[wine.typeDeVin as MetricType]);
    }
    for (const count of counts) {
      count.available += available;
      count.total += available + drunk;
    }
    if (available > 0 && isPastDrinkUntil(wine, now)) expiredBottles += available;
  }

  return { bottles, byType, expiredBottles };
}
